import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RotateCcw, CreditCard } from "lucide-react";

export default function AdminPayments() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("all"); // all | stripe | paypal | refunded | paid | pending

  const load = () => api.get("/admin/payments").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const refund = async (id) => {
    if (!window.confirm("Issue a full refund? This cannot be undone.")) return;
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/payments/${id}/refund`);
      toast.success(`Refunded via ${data.provider}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refund failed");
    } finally { setBusy(null); }
  };

  const filtered = rows.filter((r) => {
    if (filter === "all") return true;
    if (filter === "stripe" || filter === "paypal") return r.provider === filter;
    if (filter === "refunded") return r.refunded;
    if (filter === "paid") return r.payment_status === "paid" && !r.refunded;
    if (filter === "pending") return r.payment_status === "pending";
    return true;
  });

  const totals = rows.reduce((acc, r) => {
    if (r.payment_status === "paid" && !r.refunded) acc.gross += r.amount_usd || 0;
    if (r.refunded) acc.refunded += r.amount_usd || 0;
    return acc;
  }, { gross: 0, refunded: 0 });

  return (
    <div data-testid="admin-payments-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-extrabold text-3xl flex items-center gap-2"><CreditCard className="w-6 h-6" /> Payments</h1>
          <p className="text-sm text-[#4B5563]">All Stripe + PayPal transactions across the platform.</p>
        </div>
        <div className="flex gap-4">
          <div className="hs-card px-4 py-2">
            <div className="text-[10px] font-mono uppercase text-[#4B5563]">gross</div>
            <div className="font-display font-extrabold text-xl">${totals.gross.toFixed(2)}</div>
          </div>
          <div className="hs-card px-4 py-2">
            <div className="text-[10px] font-mono uppercase text-[#4B5563]">refunded</div>
            <div className="font-display font-extrabold text-xl text-[#EF4444]">-${totals.refunded.toFixed(2)}</div>
          </div>
        </div>
      </div>

      <div className="flex gap-2 border-b border-[#E2E8F0]">
        {[["all", "All"], ["paid", "Paid"], ["pending", "Pending"], ["refunded", "Refunded"], ["stripe", "Stripe"], ["paypal", "PayPal"]].map(([k, l]) => (
          <button key={k} data-testid={`payments-filter-${k}`} onClick={() => setFilter(k)} className={`px-3 py-2 text-xs font-medium border-b-2 ${filter === k ? "border-[#0044FF] text-[#030712]" : "border-transparent text-[#4B5563]"}`}>{l}</button>
        ))}
      </div>

      <div className="hs-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F4F6] text-[11px] uppercase font-mono text-[#4B5563]">
            <tr>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Tenant</th>
              <th className="text-left px-4 py-3">Provider</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Item</th>
              <th className="text-right px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-6 text-sm text-[#4B5563] text-center">No transactions.</td></tr>
            )}
            {filtered.map((p) => (
              <tr key={p.id} className="border-t border-[#E2E8F0]" data-testid={`payment-row-${p.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{p.created_at?.slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-3 text-xs">{p.tenant_name || p.tenant_id.slice(-6)}</td>
                <td className="px-4 py-3"><span className="text-[10px] font-mono px-2 py-0.5 bg-[#F3F4F6]">{p.provider}</span></td>
                <td className="px-4 py-3 text-xs">{p.type}</td>
                <td className="px-4 py-3 text-xs">{p.item_id}</td>
                <td className="px-4 py-3 text-right font-mono">${p.amount_usd?.toFixed(2)}</td>
                <td className="px-4 py-3">
                  {p.refunded ? (
                    <span className="text-[10px] font-mono px-2 py-0.5 bg-[#EF4444]/10 text-[#EF4444]">refunded</span>
                  ) : p.payment_status === "paid" ? (
                    <span className="text-[10px] font-mono px-2 py-0.5 bg-[#10B981]/10 text-[#10B981]">paid</span>
                  ) : (
                    <span className="text-[10px] font-mono px-2 py-0.5 bg-[#F3F4F6]">{p.payment_status}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {p.payment_status === "paid" && !p.refunded && p.type !== "subscription" && (
                    <button
                      onClick={() => refund(p.id)}
                      disabled={busy === p.id}
                      data-testid={`refund-button-${p.id}`}
                      className="btn-outline text-xs flex items-center gap-1 ml-auto"
                    >
                      <RotateCcw className="w-3 h-3" /> {busy === p.id ? "…" : "Refund"}
                    </button>
                  )}
                  {p.type === "subscription" && p.payment_status === "paid" && !p.refunded && (
                    <span className="text-[10px] font-mono text-[#4B5563]">refund via Stripe Dashboard</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
